import React, { useMemo } from 'react';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
// tslint:disable-next-line:import-blacklist
import { Route as RouteComponent, Switch, withRouter } from 'react-router-dom';
import { Redirect, useHistory, useRoutePath } from '@shared/routing';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import MediaQuery from 'react-responsive';
import cn from 'classnames';
import { TABLET_MIN_WIDTH } from '../../utils';
import Sidebar from '../../Sidebar';
import SimpleLoader from '../../SimpleLoader';
import { useConfig } from '../../context';
import LoginPage from '../../LoginPage';
import MobileSidebar from '../../MobileSidebar';
import { reachGoal, UserGoal } from '../../analytics';
import Orders from './Orders/Orders';
import { useTheme } from '../../theme';
import { useGetAccountInfoQuery } from '@websky/graphql';
import WarningModal from '../../WarningModal';
import { renderType } from '../../TravelCompanions/components/TravelCompanions';
import { useLogout } from '../../Modules/Account/hooks';
import { useHasLoyaltyProgram } from '../../hooks';
import { useCurrentUser } from '../../CurrentUser/CurrentUser.hooks';
import { useSelector } from 'react-redux';
import { getUnreadTotal } from '../store';
import { useRenderers } from '../../renderProps';
import { AUTH_DUPLICATES, sessionStorageGet } from '../../cache';
import Loader from '../../Loader';
import Helmet from 'react-helmet';
const HomePage = lazy(() => import('./HomePage'));
const SettingsPage = lazy(() => import('./Settings/Settings'));
const Logout = lazy(() => import('./Logout/Logout'));
const Loyalty = lazy(() => import('../../Loyalty'));
const TravelCompanions = lazy(() => import('../../TravelCompanions'));
const CommunicationPage = lazy(() => import('../../Communications'));
let routes = [
    {
        title: 'My office',
        pathname: '/account',
        icon: 'Navigation/Home'
    },
    {
        title: 'Loyalty program',
        pathname: '/account/loyalty',
        icon: 'Services/Miles'
    },
    {
        title: 'Orders',
        pathname: '/account/orders',
        icon: 'Flight/Direction'
    },
    {
        title: 'Passengers',
        pathname: '/account/companions',
        icon: 'Info/Passengers'
    },
    {
        title: 'Profile',
        pathname: '/account/settings',
        icon: 'Info/Passenger'
    },
    {
        title: 'Communications',
        pathname: '/account/communications',
        icon: 'Info/Notifications',
        hasChild: true
    }
    // {
    // 	title: i18n.t('Client support'),
    // 	pathname: '/account/support',
    // 	icon: overridedIcons['Support']
    // }
];
var LoginStages;
(function (LoginStages) {
    LoginStages[LoginStages["LoggedIn"] = 0] = "LoggedIn";
    LoginStages[LoginStages["Loading"] = 1] = "Loading";
    LoginStages[LoginStages["NotLoggedIn"] = 2] = "NotLoggedIn";
})(LoginStages || (LoginStages = {}));
const Account = ({ location }) => {
    const [isLoggedIn, setLoggedIn] = useState(LoginStages.Loading);
    const [accountInfoError, setAccountInfoError] = useState(null);
    const hasLoyaltyProgram = useHasLoyaltyProgram();
    const communicationUnreadTotal = useSelector(getUnreadTotal);
    const history = useHistory();
    const routePath = useRoutePath();
    const { Account: css } = useTheme('AccountModule');
    const { t } = useTranslation('Account');
    const { t: checkoutT } = useTranslation('Checkout');
    const { AccountLoyalty: AccountLoyaltyOverride = Loyalty } = useRenderers();
    const currentUser = useGetAccountInfoQuery({
        variables: {
            skipOrdersList: true
        },
        fetchPolicy: 'no-cache'
    });
    const { onLogout, loading: logoutLoading } = useLogout('/account');
    const [isLoading, setLoading] = React.useState(false);
    const { Account: { engineURL, showCommunications }, global: { companyInfo } } = useConfig();
    useCurrentUser();
    useEffect(() => {
        var _a, _b;
        if ((_b = (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.data) === null || _a === void 0 ? void 0 : _a.CurrentUser) === null || _b === void 0 ? void 0 : _b.authMethods) {
            setLoggedIn(LoginStages.LoggedIn);
        }
        else if (currentUser.loading) {
            setLoggedIn(LoginStages.Loading);
        }
        else {
            setLoggedIn(LoginStages.NotLoggedIn);
        }
        if (currentUser === null || currentUser === void 0 ? void 0 : currentUser.error) {
            setAccountInfoError(currentUser.error.message);
        }
    }, [currentUser]);
    const [userSettingsError, setUserSettingsError] = useState(null);
    const userInfo = useMemo(() => {
        var _a;
        const userValues = {
            email: '',
            firstName: '',
            lastName: ''
        };
        if (!(currentUser === null || currentUser === void 0 ? void 0 : currentUser.loading) && ((_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.data) === null || _a === void 0 ? void 0 : _a.CurrentUser)) {
            currentUser.data.CurrentUser.userProfile.values.forEach(value => {
                if (value.name === 'lastName' || value.name === 'firstName' || value.name === 'email') {
                    userValues[value.name] = value.value;
                }
            });
        }
        return userValues;
    }, [currentUser]);
    const refetchAccountInfo = () => {
        setLoading(true);
        currentUser.refetch().finally(() => setLoading(false));
    };
    const clearUserSettingsError = useCallback(() => {
        setUserSettingsError(null);
    }, []);
    const onLoyaltyCardSaved = () => {
        setTimeout(() => {
            refetchAccountInfo();
        }, 2000);
        return new Promise(res => res(true));
    };
    routes = routes.filter(route => {
        const pathRoute = route;
        if (pathRoute.pathname === '/account/loyalty') {
            return hasLoyaltyProgram;
        }
        if (pathRoute.pathname === '/account/communications') {
            return showCommunications;
        }
        return true;
    });
    routes.find(route => {
        var _a, _b;
        const pathRoute = route;
        if (pathRoute.pathname === '/account/loyalty') {
            route.title = checkoutT(companyInfo === null || companyInfo === void 0 ? void 0 : companyInfo.loyaltyProgramName);
            if ((_a = companyInfo === null || companyInfo === void 0 ? void 0 : companyInfo.loyalty) === null || _a === void 0 ? void 0 : _a.redirectTo) {
                pathRoute.pathname = (_b = companyInfo === null || companyInfo === void 0 ? void 0 : companyInfo.loyalty) === null || _b === void 0 ? void 0 : _b.redirectTo;
                pathRoute.isRedirect = true;
            }
        }
        if (pathRoute.pathname === '/account/communications') {
            pathRoute.badgeContent = communicationUnreadTotal;
        }
    });
    useEffect(() => {
        routes = [
            ...routes,
            {
                title: i18n.t('Logout'),
                icon: 'Navigation/Logout',
                action: onLogout
            }
        ];
    }, []);
    useEffect(() => {
        if (isLoggedIn === LoginStages.NotLoggedIn) {
            history.replace('/account');
        }
    }, [isLoggedIn]);
    const onCloseAccountInfoError = () => {
        setAccountInfoError(null);
    };
    const onGoToSettings = () => {
        history.push('/account/settings');
    };
    useEffect(() => {
        var _a;
        if (!!sessionStorageGet(AUTH_DUPLICATES) && ((_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.data) === null || _a === void 0 ? void 0 : _a.CurrentUser)) {
            onGoToSettings();
        }
    }, [currentUser]);
    /**
     * Костыль для WEBSKY-8068:
     * На внутренних странницах раздела коммуникаций, должен быть другой сайдбар и другие оступы.
     * Прячем стилями(opacity: 0; pointer-events: none;), чтобы сохранить отступы.
     */
    const isInnerCommunicationsPage = useMemo(() => {
        if (showCommunications) {
            return /^\/account\/communications\/.+/.test(location.pathname);
        }
        return false;
    }, [location.pathname, showCommunications]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            React.createElement("title", null,
                t('Title:companyName'),
                " \u2013 ",
                t('Account'))),
        logoutLoading && React.createElement(SimpleLoader, null),
        isLoggedIn === LoginStages.LoggedIn && (React.createElement("div", { className: css.wrapper },
            React.createElement(MediaQuery, { minWidth: +TABLET_MIN_WIDTH + 1 },
                React.createElement(Sidebar, { routes: routes, selectedRoute: location.pathname, userInfo: userInfo })),
            React.createElement(MediaQuery, { maxWidth: TABLET_MIN_WIDTH },
                React.createElement(MobileSidebar, { className: cn(css.mobileSidebar, { [css.mobileSidebar__hide]: isInnerCommunicationsPage }), routes: routes, selectedRoute: location.pathname })),
            React.createElement("div", { className: cn(css.active__route, {
                    [css.active__route__communications]: isInnerCommunicationsPage
                }) },
                React.createElement(Suspense, { fallback: React.createElement(Loader, null) },
                    React.createElement(Switch, null,
                        React.createElement(RouteComponent, { path: routePath('/account'), exact: true, render: () => (React.createElement(HomePage, { currentUser: currentUser.data.CurrentUser.userProfile })) }),
                        React.createElement(RouteComponent, { path: routePath('/account/orders'), exact: true, render: () => React.createElement(Orders, null) }),
                        React.createElement(RouteComponent, { path: routePath('/account/profile'), exact: true, render: () => React.createElement(Redirect, { to: "/account/companions" }) }),
                        React.createElement(RouteComponent, { path: routePath('/account/settings'), render: () => {
                                var _a;
                                return (React.createElement(SettingsPage, { currentUser: (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.data) === null || _a === void 0 ? void 0 : _a.CurrentUser, setUserSettingsError: setUserSettingsError, refetchAccountInfo: refetchAccountInfo }));
                            } }),
                        React.createElement(RouteComponent, { path: routePath('/account/logout'), exact: true, render: () => {
                                if (isLoggedIn === LoginStages.LoggedIn) {
                                    return React.createElement(Logout, null);
                                }
                                else if (isLoggedIn === LoginStages.NotLoggedIn) {
                                    return React.createElement(Redirect, { to: "/account" });
                                }
                                else {
                                    return React.createElement(Loader, null);
                                }
                            } }),
                        hasLoyaltyProgram && (React.createElement(RouteComponent, { path: routePath('/account/loyalty'), exact: true, render: () => {
                                var _a, _b, _c, _d, _e;
                                return (React.createElement(React.Fragment, null,
                                    React.createElement("h1", { className: css.route__title }, checkoutT(companyInfo === null || companyInfo === void 0 ? void 0 : companyInfo.loyaltyProgramName)),
                                    React.createElement(AccountLoyaltyOverride, { isAuthorizationNeeded: (_b = (_a = currentUser.data) === null || _a === void 0 ? void 0 : _a.CurrentUser) === null || _b === void 0 ? void 0 : _b.isFfpAuthorizationNeeded, links: (_c = companyInfo === null || companyInfo === void 0 ? void 0 : companyInfo.loyalty) === null || _c === void 0 ? void 0 : _c.links, ffpInfo: (_e = (_d = currentUser.data) === null || _d === void 0 ? void 0 : _d.CurrentUser) === null || _e === void 0 ? void 0 : _e.userFfpInfo, loyaltyName: checkoutT(companyInfo === null || companyInfo === void 0 ? void 0 : companyInfo.loyaltyProgramName), onLoyaltyCardSaved: onLoyaltyCardSaved, userInfo: userInfo, onGoToSettings: onGoToSettings, isLoading: isLoading })));
                            } })),
                        React.createElement(RouteComponent, { path: routePath('/account/companions'), exact: true, render: () => (React.createElement(React.Fragment, null,
                                React.createElement(TravelCompanions, { format: renderType.Companions }))) }),
                        showCommunications && (React.createElement(RouteComponent, { path: routePath('/account/communications'), render: () => React.createElement(CommunicationPage, null) })),
                        React.createElement(RouteComponent, { path: routePath('/'), render: () => {
                                window.location.href = `${engineURL}#/404`;
                                return null;
                            } })))))),
        isLoggedIn === LoginStages.NotLoggedIn && (React.createElement("div", { className: css.loginWrapper },
            React.createElement(LoginPage, { onSuccess: isRegistration => {
                    reachGoal(isRegistration ? UserGoal.Registration : UserGoal.Login);
                    window.location.reload();
                } }),
            React.createElement(WarningModal, { isOpen: !!accountInfoError, onClose: onCloseAccountInfoError, onButtonClick: onCloseAccountInfoError, errorMessage: accountInfoError, title: t('Oops...'), content: t('An error occurred while executing the request. Please try again later or contact a customer support service.'), buttonText: t('Ok') }))),
        React.createElement(WarningModal, { title: t('Oops...'), content: t('An error occurred while executing the request. Please try again later or contact a customer support service.'), errorMessage: userSettingsError ? userSettingsError.message : '', onClose: clearUserSettingsError, onButtonClick: clearUserSettingsError, buttonText: t('OK, thank you'), isOpen: !!userSettingsError })));
};
export default withRouter(Account);
